import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Copy, Check, Users, ShoppingBag, TrendingUp, Wallet, MousePointer, Eye, CreditCard, BarChart3, Calendar, Fish, MessageCircle, QrCode } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { apiFetchJson } from '../lib/api';
import { formatPrice, maskPhone } from '../lib/utils';
import { trackPageView, trackEvent } from '../lib/analytics';
import type { ReferralDashboardData } from '../types';
import { SITE_NAME } from '../lib/brand';

const STATUS_LABELS: Record<string, string> = {
  pending: '待確認',
  approved: '已確認',
  paid: '已發放',
  cancelled: '已取消',
};

const DAY_OPTIONS = [
  { value: 7, label: '最近 7 天' },
  { value: 30, label: '最近 30 天' },
  { value: 90, label: '最近 90 天' },
];

export default function ReferralDashboard() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<ReferralDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [copiedBait, setCopiedBait] = useState<string | null>(null);
  const [qrOpen, setQrOpen] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [days, setDays] = useState(30);

  useEffect(() => {
    trackPageView('/ref/d/:token');
    if (!token) {
      setLoading(false);
      return;
    }
    apiFetchJson<ReferralDashboardData>(`/public/referral/dashboard/${token}?days=${days}`)
      .then((res) => {
        if (res.success && res.data) {
          setData(res.data);
        } else {
          setError(res.error || '找不到夥伴資料');
        }
        setLoading(false);
      })
      .catch(() => {
        setError('載入失敗');
        setLoading(false);
      });
  }, [token, days]);

  const handleCopy = async () => {
    if (!data?.referralLink) return;
    await navigator.clipboard.writeText(data.referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // 魚餌文案：活動 headline＋subline＋夥伴個人化連結（wa.me 分享／複製共用）
  const baitText = (headline: string, subline: string, link: string) =>
    `🌶️ ${headline}！${subline} 👉 ${link}`;

  const handleCopyBait = async (key: string, text: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedBait(key);
    trackEvent('bait_share_copy', { campaignKey: key });
    setTimeout(() => setCopiedBait(null), 2000);
  };

  const handleWhatsAppShare = (key: string, text: string) => {
    trackEvent('bait_share_whatsapp', { campaignKey: key });
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="text-center py-20">載入中...</div>
        <Footer />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-bold mb-4">{error || '無效的連結'}</h1>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="bg-x1493-dark text-white py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">{SITE_NAME} 夥伴 Dashboard</h1>
          <p className="text-white/80">歡迎加入推薦夥伴計劃</p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 space-y-8">
          {/* Greeting */}
          <div className="bg-gradient-to-r from-x1493-primary to-x1493-secondary rounded-2xl p-6 text-white">
            <h2 className="text-2xl font-bold mb-1">{data.name}，你好！</h2>
            <p className="text-white/90">謝謝你將火熱分享給更多人。</p>
          </div>

          {/* Referral Link */}
          <div className="bg-stone-50 rounded-2xl p-6">
            <h3 className="font-bold text-lg mb-3">你的專屬連結</h3>
            <div className="flex gap-2">
              <input
                type="text"
                readOnly
                value={data.referralLink}
                className="flex-1 p-3 border border-stone-200 rounded-xl bg-white text-stone-600 text-sm"
              />
              <button
                onClick={handleCopy}
                className="bg-x1493-dark text-white px-4 py-2 rounded-xl font-bold hover:bg-black transition flex items-center gap-2"
              >
                {copied ? <Check size={18} /> : <Copy size={18} />}
                {copied ? '已複製' : '複製'}
              </button>
            </div>
          </div>

          {/* 推廣魚餌：平台活動 x 夥伴個人化連結（/gf/{短碼}?r=碼 → 登記/落單終身綁定本夥伴） */}
          {data.campaigns && data.campaigns.length > 0 && (
            <div className="rounded-2xl p-6 text-white" style={{ background: 'linear-gradient(120deg, #0b3527 0%, #12503c 60%, #1a6b50 100%)' }}>
              <div className="flex items-center gap-2 mb-1">
                <Fish size={22} className="text-x1493-secondary" />
                <h3 className="text-xl font-bold">推廣魚餌</h3>
              </div>
              <p className="text-white/70 text-sm mb-5">平台做好咗釣餌——你只需要分享，朋友登記／落單都終身計你數。</p>
              <div className="space-y-5">
                {data.campaigns.map((c) => {
                  const shareText = baitText(c.headline || c.key, c.subline || '', c.baitLink);
                  const isCopied = copiedBait === c.key;
                  const isQrOpen = qrOpen === c.key;
                  return (
                    <div key={c.key} className="bg-white/10 border border-white/15 rounded-2xl p-5">
                      <p className="font-bold mb-1">🎣 {c.headline || c.key}</p>
                      {c.subline && <p className="text-white/70 text-sm mb-4">{c.subline}</p>}
                      <div className="flex flex-col sm:flex-row gap-2 mb-3">
                        <input
                          type="text"
                          readOnly
                          value={c.baitLink}
                          onFocus={(e) => e.target.select()}
                          className="flex-1 p-2.5 rounded-xl bg-white/95 text-stone-700 text-sm font-mono"
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleCopyBait(c.key, shareText)}
                            className="flex-1 sm:flex-none bg-x1493-secondary text-x1493-dark px-4 py-2.5 rounded-xl font-bold text-sm hover:brightness-105 transition flex items-center justify-center gap-1.5"
                          >
                            {isCopied ? <Check size={16} /> : <Copy size={16} />}
                            {isCopied ? '已複製文案' : '複製文案＋連結'}
                          </button>
                          <button
                            onClick={() => handleWhatsAppShare(c.key, shareText)}
                            className="flex-1 sm:flex-none bg-[#25D366] text-white px-4 py-2.5 rounded-xl font-bold text-sm hover:brightness-105 transition flex items-center justify-center gap-1.5"
                          >
                            <MessageCircle size={16} /> WhatsApp 分享
                          </button>
                          <button
                            onClick={() => setQrOpen(isQrOpen ? null : c.key)}
                            className={`px-3 py-2.5 rounded-xl font-bold text-sm transition flex items-center justify-center gap-1.5 border ${isQrOpen ? 'bg-white text-x1493-primary border-white' : 'border-white/40 text-white hover:bg-white/10'}`}
                            title="顯示 QR code"
                          >
                            <QrCode size={16} /> QR
                          </button>
                        </div>
                      </div>
                      {isQrOpen && (
                        <div className="bg-white rounded-xl p-4 inline-flex flex-col items-center gap-2">
                          <QRCodeSVG value={c.baitLink} size={148} level="M" />
                          <p className="text-xs text-stone-500 text-center">朋友掃碼直達魚餌頁（已帶你嘅碼）</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <p className="text-white/50 text-xs mt-4">* 朋友首次經你連結入場後即終身綁定；佢日後每次消費，回贈都計你數。</p>
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-stone-50 rounded-2xl p-5 text-center">
              <Users className="mx-auto text-x1493-secondary mb-2" size={28} />
              <p className="text-2xl font-bold">{data.totalBoundCustomers}</p>
              <p className="text-xs text-stone-500">綁定顧客</p>
            </div>
            <div className="bg-stone-50 rounded-2xl p-5 text-center">
              <ShoppingBag className="mx-auto text-x1493-secondary mb-2" size={28} />
              <p className="text-2xl font-bold">{data.totalOrders}</p>
              <p className="text-xs text-stone-500">成交訂單</p>
            </div>
            <div className="bg-stone-50 rounded-2xl p-5 text-center">
              <TrendingUp className="mx-auto text-x1493-secondary mb-2" size={28} />
              <p className="text-2xl font-bold">{data.approvedCommission}</p>
              <p className="text-xs text-stone-500">已確認回贈（分）</p>
            </div>
            <div className="bg-stone-50 rounded-2xl p-5 text-center">
              <Wallet className="mx-auto text-x1493-secondary mb-2" size={28} />
              <p className="text-2xl font-bold">{data.paidCommission}</p>
              <p className="text-xs text-stone-500">已發放回贈（分）</p>
            </div>
          </div>

          {/* Traffic Analytics */}
          <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <BarChart3 className="text-x1493-secondary" size={24} />
                流量與轉化
              </h3>
              <div className="flex items-center gap-2">
                <Calendar size={16} className="text-stone-500" />
                <select
                  value={days}
                  onChange={(e) => setDays(Number(e.target.value))}
                  className="px-3 py-1.5 border border-stone-200 rounded-lg text-sm bg-white text-stone-700 focus:ring-2 focus:ring-x1493-primary outline-none"
                >
                  {DAY_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-stone-50 rounded-xl p-4 text-center">
                <MousePointer className="mx-auto text-x1493-secondary mb-1" size={22} />
                <p className="text-xl font-bold">{data.traffic?.clicks ?? 0}</p>
                <p className="text-xs text-stone-500">連結獨立點擊</p>
              </div>
              <div className="bg-stone-50 rounded-xl p-4 text-center">
                <Eye className="mx-auto text-x1493-secondary mb-1" size={22} />
                <p className="text-xl font-bold">{data.traffic?.uniqueVisitors ?? 0}</p>
                <p className="text-xs text-stone-500">獨立訪客</p>
              </div>
              <div className="bg-stone-50 rounded-xl p-4 text-center">
                <ShoppingBag className="mx-auto text-x1493-secondary mb-1" size={22} />
                <p className="text-xl font-bold">{data.traffic?.orders ?? 0}</p>
                <p className="text-xs text-stone-500">成交訂單</p>
              </div>
              <div className="bg-stone-50 rounded-xl p-4 text-center">
                <CreditCard className="mx-auto text-x1493-secondary mb-1" size={22} />
                <p className="text-xl font-bold">{formatPrice(data.traffic?.revenue ?? 0)}</p>
                <p className="text-xs text-stone-500">帶來營收</p>
              </div>
            </div>

            {/* Conversion Funnel */}
            <div className="mb-6">
              <h4 className="text-sm font-bold text-stone-700 mb-3">轉化漏斗</h4>
              <div className="space-y-2">
                {[
                  { label: '獨立點擊', value: data.traffic?.clicks ?? 0, color: 'bg-blue-500' },
                  { label: '瀏覽頁面', value: data.traffic?.pageViews ?? 0, color: 'bg-indigo-500' },
                  { label: '加入購物車', value: data.traffic?.addToCarts ?? 0, color: 'bg-purple-500' },
                  { label: '開始結帳', value: data.traffic?.beginCheckouts ?? 0, color: 'bg-pink-500' },
                  { label: '完成訂單', value: data.traffic?.orders ?? 0, color: 'bg-green-500' },
                ].map((step, idx, arr) => {
                  const max = Math.max(1, arr[0].value);
                  const prev = idx > 0 ? arr[idx - 1].value : step.value;
                  const rate = prev ? ((step.value / prev) * 100).toFixed(1) + '%' : '0%';
                  return (
                    <div key={step.label} className="flex items-center gap-3">
                      <div className="w-24 text-xs text-stone-500 text-right">{step.label}</div>
                      <div className="flex-1 h-8 bg-stone-100 rounded-lg overflow-hidden relative">
                        <div
                          className={`h-full ${step.color} transition-all duration-500`}
                          style={{ width: `${Math.min(100, (step.value / max) * 100)}%` }}
                        />
                        <span className="absolute inset-0 flex items-center px-2 text-xs font-medium text-stone-700">
                          {step.value.toLocaleString('zh-HK')} {idx > 0 && <span className="ml-2 text-stone-400">({rate})</span>}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-3 flex items-center justify-between text-sm text-stone-600">
                <span>整體點擊轉化率：<span className="font-bold text-x1493-primary">{data.traffic?.clickToOrderRate ?? '0%'}</span></span>
                <span>訪客轉化率：<span className="font-bold text-x1493-primary">{data.traffic?.visitorToOrderRate ?? '0%'}</span></span>
              </div>
            </div>

            {/* Top Pages */}
            {data.topPages && data.topPages.length > 0 && (
              <div>
                <h4 className="text-sm font-bold text-stone-700 mb-3">熱門頁面</h4>
                <div className="space-y-2">
                  {data.topPages.map((page, idx) => {
                    let displayPage = page.page || '/';
                    try { displayPage = decodeURIComponent(displayPage); } catch { /* keep original */ }
                    return (
                      <div key={page.page + idx} className="flex items-center justify-between text-sm">
                        <span className="text-stone-600 truncate max-w-[70%]">{displayPage}</span>
                        <span className="font-medium text-stone-800">{page.views} 次瀏覽</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Withdrawal Notice */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 text-center">
            <p className="text-amber-800 font-medium">100分等於 HK$1，需要兌換請 WhatsApp 聯絡客服 🌶️</p>
          </div>

          {/* Recent Orders */}
          <div>
            <h3 className="text-xl font-bold mb-4">最近成交紀錄</h3>
            {data.recentOrders.length === 0 ? (
              <p className="text-stone-500">暫無成交紀錄</p>
            ) : (
              <div className="space-y-3">
                {data.recentOrders.map((order) => (
                  <div key={order.orderNum} className="bg-stone-50 rounded-xl p-4 flex flex-col sm:flex-row justify-between gap-2">
                    <div>
                      <p className="font-bold">#{order.orderNum}</p>
                      <p className="text-sm text-stone-500">{maskPhone(order.customerPhone)}</p>
                    </div>
                    <div className="text-left sm:text-right">
                      <p className="font-bold text-x1493-primary">{formatPrice(order.orderAmount)}</p>
                      <p className="text-sm text-stone-600">回贈 {order.commissionPoints} 分</p>
                      <span className="inline-block mt-1 bg-white px-2 py-0.5 rounded text-xs font-bold text-stone-600">
                        {STATUS_LABELS[order.status] || order.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
